import { useEffect, useRef } from 'react';
import { useKryptonClient } from '../hooks';
import './smartform-embed.css';

/**
 * Wrapper React autour du SmartForm Krypton (Lyra / SogeCommerce).
 */
export interface SmartFormEmbedProps {
    formToken: string;
    publicKey: string;
    endpoint?: string | null;
    onPaid: () => void;
    onError?: (message: string) => void;
}

declare global {
    interface Window {
        KR?: any;
    }
}

export function SmartFormEmbed({ formToken, publicKey, endpoint, onPaid, onError }: SmartFormEmbedProps) {
    const containerRef = useRef<HTMLDivElement | null>(null);
    const { ready, error } = useKryptonClient({ publicKey, endpoint });

    const onPaidRef = useRef(onPaid);
    const onErrorRef = useRef(onError);
    onPaidRef.current = onPaid;
    onErrorRef.current = onError;

    useEffect(() => {
        if (!ready || !formToken || !window.KR || !containerRef.current) return;

        let cancelled = false;

        (async () => {
            try {
                window.KR.onSubmit((event: any) => {
                    if (event?.clientAnswer?.orderStatus === 'PAID') {
                        onPaidRef.current();
                    }
                    return false;
                });

                window.KR.onError((event: any) => {
                    onErrorRef.current?.(event?.errorMessage || 'Erreur du module de paiement.');
                });

                // En mode kr-smart-form, Krypton auto-monte le formulaire sur
                // setFormConfig — pas besoin d'addForm()/showForm().
                await window.KR.setFormConfig({ publicKey, formToken });
                if (cancelled) return;
            } catch (e: any) {
                console.error('[SmartFormEmbed] init error:', e);
                onErrorRef.current?.(e?.message || 'Erreur lors de l\'initialisation du SmartForm.');
            }
        })();

        return () => { cancelled = true; };
    }, [ready, formToken, publicKey]);

    if (error) {
        return (
            <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
                {error.message}
            </div>
        );
    }

    return (
        <div
            ref={containerRef}
            id="smartform-embed-container"
            className="kr-smart-form"
            // Attribut du widget Krypton/Lyra, inconnu d'ESLint mais transmis au DOM par React.
            // eslint-disable-next-line react/no-unknown-property
            kr-card-form-expanded=""
        />
    );
}
